import { useState, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard, Plus, MapPin, User, Wallet, Bell,
  LogOut, Menu, X, Crown, Store, Shield
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import krewLogo from "@/assets/krew-logo.png";
import ThemeToggle from "@/components/ThemeToggle";

const AppLayout = () => {
  const { user, profile, isVendor, isAdmin, signOut } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Plus, label: "Create Krew", path: "/create-krew" },
    { icon: MapPin, label: "Venues", path: "/venues" },
    { icon: Wallet, label: "Wallet", path: "/wallet" },
    { icon: User, label: "Profile", path: "/profile" },
    ...(isVendor ? [{ icon: Store, label: "Vendor Portal", path: "/vendor" }] : []),
    ...(isAdmin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      setUnreadCount(count || 0);
    };
    fetchUnread();

    const channel = supabase
      .channel("notif-badge")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => fetchUnread())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-16 items-center gap-2 px-6">
          <img src={krewLogo} alt="Krew" className="h-8 w-8" />
          <Link to="/dashboard" className="text-2xl font-bold text-gradient-logo">Krew</Link>
          {profile?.is_premium && <Crown className="ml-1 h-4 w-4 text-secondary" />}
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                <item.icon className="h-5 w-5" />{item.label}
              </Link>
            );
          })}
          <Link to="/notifications"
            className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${location.pathname === "/notifications" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
            <div className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </div>
            Notifications
          </Link>
        </nav>

        <div className="border-t border-border p-4">
          <div className="mb-3 flex items-center gap-3">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/20 text-sm font-bold text-primary">
                {profile?.display_name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 truncate">
              <p className="truncate text-sm font-medium text-foreground">{profile?.display_name}</p>
              <p className="text-xs text-muted-foreground">Score: {profile?.reliability_score}%</p>
            </div>
          </div>
          <button onClick={signOut} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <LogOut className="h-4 w-4" />Sign Out
          </button>
          <div className="mt-2">
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-card px-4 lg:hidden">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={krewLogo} alt="Krew" className="h-7 w-7" />
          <span className="text-xl font-bold text-gradient-logo">Krew</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/notifications" className="relative p-2 text-muted-foreground hover:text-foreground">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">{unreadCount > 9 ? "9+" : unreadCount}</span>
            )}
          </Link>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-foreground">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, x: "100%" }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: "100%" }} className="fixed inset-0 z-50 bg-card pt-14 lg:hidden">
            <nav className="space-y-1 p-4">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3.5 text-base font-medium transition-all ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                    <item.icon className="h-5 w-5" />{item.label}
                  </Link>
                );
              })}
              <button onClick={() => { signOut(); setMobileOpen(false); }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-base text-muted-foreground hover:bg-muted hover:text-foreground">
                <LogOut className="h-5 w-5" />Sign Out
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="flex-1 pt-14 lg:ml-64 lg:pt-0">
        <div className="mx-auto max-w-6xl p-6"><Outlet /></div>
      </main>
    </div>
  );
};

export default AppLayout;
